import _debounce from 'lodash/debounce';

type HttpMethod = 'post' | 'delete';

/**
 * リアクションタイプの値:
 * - 'none' → いいね（リアクション）されていない状態。押下でいいね（リアクション）される。
 * - 'like' → いいねされている状態。
 * - カスタムリアクションの url_name → 該当リアクションが行われている状態。
 */
type ReactionType = string;

const RELATED_BUTTON_SELECTOR = '.js-like, .js-like-sns';

export function determineReactionType(
  hideReactionType: ReactionType,
  clickedReactionType: ReactionType
): ReactionType {
  if (hideReactionType === 'none' && clickedReactionType === 'none')
    return 'like';
  if (hideReactionType === clickedReactionType) return 'none';
  return clickedReactionType;
}

function handleAnimationEnd(event: Event): void {
  const target = event.target;
  if (!(target instanceof Element)) return;
  setTimeout(() => {
    target.classList.add('like-anime-hidden');
    target.classList.remove('like-animation');
  }, 500);
}

function isMobileViewport(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-device-width: 640px)').matches
  );
}

function getCsrfToken(): string {
  return (
    document.querySelector<HTMLMetaElement>('meta[name=csrf-token]')?.content ??
    ''
  );
}

export default class Like {
  private readonly classname: string;
  private readonly snsAnimation: boolean;
  private readonly buttons: NodeListOf<HTMLElement>;
  private hideEvent: ReturnType<typeof setTimeout> | null = null;
  private longTouchEvent: ReturnType<typeof setTimeout> | null = null;

  constructor(
    classname: string,
    snsAnimation = false,
    rootElement: Document | Element = document
  ) {
    this.classname = classname;
    this.snsAnimation = snsAnimation;
    this.buttons = rootElement.querySelectorAll<HTMLElement>(classname);

    this.buttons.forEach((button) => {
      this.bindClick(button);
      this.bindHover(button);
      this.bindTouch(button);
    });
  }

  private findPopup(button: HTMLElement): HTMLElement | null {
    return document.querySelector<HTMLElement>(
      `${this.classname}-popup[data-value="${button.dataset.value}"]`
    );
  }

  private findEyecatch(button: HTMLElement): HTMLElement | null {
    return document.querySelector<HTMLElement>(
      `${this.classname}-animation[data-value="${button.dataset.value}"]`
    );
  }

  private bindHover(button: HTMLElement): void {
    const popup = this.findPopup(button);
    if (!popup) return;

    button.addEventListener('mouseout', () => this.popupHide(popup));
    button.addEventListener('mouseover', () => {
      this.popupShowWithTimer(popup);
      popup.addEventListener('mouseover', () => this.popupShow(popup));
      popup.addEventListener('mouseout', () => this.popupHide(popup));
    });
  }

  private bindClick(button: HTMLElement): void {
    const eyecatch = this.findEyecatch(button);
    const popup = this.findPopup(button);

    if (this.snsAnimation && eyecatch) {
      eyecatch.removeEventListener('animationend', handleAnimationEnd);
      eyecatch.addEventListener('animationend', handleAnimationEnd);
    }

    button.addEventListener(
      'click',
      _debounce(() => {
        void this.handleClick(button, eyecatch, popup);
      }, 400)
    );
  }

  private async handleClick(
    clickedButton: HTMLElement,
    eyecatch: HTMLElement | null,
    popup: HTMLElement | null
  ): Promise<void> {
    // debounce 待機中に別ボタンの先行リクエストで disabled 化されていた場合、二重実行を防ぐ
    if (
      clickedButton instanceof HTMLButtonElement &&
      clickedButton.disabled
    ) {
      return;
    }

    // 詳細画面でリアクションポップアップ内のボタンを押下した場合、非表示対象をいいねボタンにする
    const hideButton = clickedButton.classList.contains('c-Button-reaction')
      ? document.querySelector<HTMLElement>(
          `.c-Button-like:not(.hidden)[data-key="${clickedButton.dataset.key}"][data-value="${clickedButton.dataset.value}"]`
        )
      : clickedButton;
    if (!hideButton) return;

    const parent = clickedButton.parentElement;
    if (!parent) return;
    // popup 内 click では hideButton (main .c-Button-like) が parent スコープ外なので明示的に含める
    const relatedButtons = Array.from(
      parent.querySelectorAll<HTMLButtonElement>(RELATED_BUTTON_SELECTOR)
    );
    if (
      hideButton instanceof HTMLButtonElement &&
      !relatedButtons.includes(hideButton)
    ) {
      relatedButtons.push(hideButton);
    }
    relatedButtons.forEach((btn) => (btn.disabled = true));

    try {
      const displayReactionType = determineReactionType(
        hideButton.dataset.reactionType ?? 'none',
        clickedButton.dataset.reactionType ?? 'none'
      );
      const method: HttpMethod =
        displayReactionType === 'none' ? 'delete' : 'post';

      this.updateUI(
        hideButton,
        displayReactionType,
        popup,
        method,
        eyecatch
      );
      const response = await this.sendReactionRequest(
        clickedButton.dataset,
        method
      );
      if (!response.ok) console.error('Like request failed');
    } catch (error) {
      console.error('Like request failed:', error);
    } finally {
      relatedButtons.forEach((btn) => (btn.disabled = false));
    }
  }

  private updateUI(
    hideButton: HTMLElement,
    displayReactionType: ReactionType,
    popup: HTMLElement | null,
    method: HttpMethod,
    eyecatch: HTMLElement | null
  ): void {
    if (popup) popup.classList.add('popup_hide');

    const displayButton = this.findDisplayButton(
      hideButton,
      displayReactionType
    );
    if (!displayButton) return;

    this.toggleVisibility(
      hideButton,
      displayButton,
      method === 'post',
      eyecatch
    );
  }

  private findDisplayButton(
    baseElem: HTMLElement,
    reactionType: ReactionType
  ): HTMLElement | null {
    const parent = baseElem.parentElement;
    if (!parent) return null;
    return (
      Array.from(parent.children).find(
        (child): child is HTMLElement =>
          child instanceof HTMLElement &&
          child !== baseElem &&
          child.dataset.reactionType === reactionType
      ) ?? null
    );
  }

  private toggleVisibility(
    hideButton: HTMLElement,
    displayButton: HTMLElement,
    isPostAction: boolean,
    eyecatch: HTMLElement | null
  ): void {
    hideButton.classList.add('hidden');
    displayButton.classList.remove('hidden');

    if (!this.snsAnimation) return;

    if (isPostAction) {
      hideButton.classList.remove('-unfilled');
      displayButton.classList.add('-filled');
      if (eyecatch) {
        eyecatch.classList.remove('like-anime-hidden');
        eyecatch.classList.add('like-animation');
      }
    } else {
      hideButton.classList.remove('-filled');
      displayButton.classList.add('-unfilled');
    }
  }

  private async sendReactionRequest(
    dataset: DOMStringMap,
    method: HttpMethod
  ): Promise<Response> {
    const reactionParam =
      dataset.reactionType &&
      !['none', 'like'].includes(dataset.reactionType)
        ? `&like_reaction_attributes[community_reaction_id]=${dataset.reactionType}`
        : '';

    return fetch(
      `${dataset.url}?likeable_type=${dataset.key}&likeable_id=${dataset.value}${reactionParam}`,
      {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': getCsrfToken(),
        },
      }
    );
  }

  private bindTouch(button: HTMLElement): void {
    if (!isMobileViewport()) return;
    const popup = this.findPopup(button);
    if (!popup) return;

    button.addEventListener('touchstart', (event) => {
      event.preventDefault();
      this.longTouchEvent = setTimeout(() => {
        this.popupShowWithTimer(popup, 5000);
        this.longTouchEvent = null;
      }, 500);
    });

    button.addEventListener('touchend', () => {
      if (this.longTouchEvent !== null) {
        clearTimeout(this.longTouchEvent);
        button.click();
      }
      const reactionButtons = document.querySelectorAll<HTMLElement>(
        `.c-Button-reaction[data-value="${button.dataset.value}"]`
      );
      reactionButtons.forEach((reactionButton) => {
        if (reactionButton.classList.contains('hovered')) {
          reactionButton.click();
          reactionButton.classList.remove('hovered');
        }
      });
      this.popupHide(popup);
    });

    button.addEventListener('touchmove', (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      const targetElement = document.elementFromPoint(
        touch.clientX,
        touch.clientY
      );
      const reactionButtons = document.querySelectorAll<HTMLElement>(
        `.c-Button-reaction[data-value="${button.dataset.value}"]`
      );
      reactionButtons.forEach((reactionButton) => {
        if (
          targetElement &&
          reactionButton === targetElement.closest('.c-Button-reaction')
        ) {
          reactionButton.classList.add('hovered');
        } else {
          reactionButton.classList.remove('hovered');
        }
      });
    });
  }

  private popupShowWithTimer(popup: HTMLElement, duration = 1000): void {
    popup.classList.remove('popup_hide');
    this.popupHide(popup, duration);
  }

  private popupShow(popup: HTMLElement): void {
    if (this.hideEvent !== null) clearTimeout(this.hideEvent);
    popup.classList.remove('popup_hide');
  }

  private popupHide(popup: HTMLElement, duration = 1000): void {
    if (this.hideEvent !== null) clearTimeout(this.hideEvent);
    this.hideEvent = setTimeout(() => {
      popup.classList.add('popup_hide');
    }, duration);
  }
}
